import { action, computed, makeObservable, observable } from 'mobx';
import { RatesOverriderDelegate } from 'store/modules/override-rates/ratesOverriderDelegate';
import { OverrideRatesUwProduct, UwProductRatesForm } from './uwProductRatesForm';

interface Props {
  uwProducts: OverrideRatesUwProduct[];
}

export class UwRatesOverriderDelegate extends RatesOverriderDelegate {
  private _selectedRateSource: 'original' | 'final' = 'final';
  private _cannotSelectFinalRates: boolean = false;
  public rateForms: UwProductRatesForm[] = [];

  get selectedRateSource(): 'original' | 'final' {
    return this._selectedRateSource;
  }

  get cannotSelectFinalRates(): boolean {
    return this._cannotSelectFinalRates;
  }

  private _setCannotSelectFinalRates(value: boolean): void {
    this._cannotSelectFinalRates = value;
  }

  public setRateSource(source: 'original' | 'final'): void {
    this._selectedRateSource = source;
    this.restoreInitialRates();
  }

  public restoreInitialRates(): void {
    this.rateForms.forEach((rateForm) => {
      rateForm.setRateSource(this._selectedRateSource);
      rateForm.resetToInitialRates();
    });
  }

  public createRateForms(uwProducts: OverrideRatesUwProduct[]): void {
    const finalRatesIneligible = uwProducts[0].final.rates.length < 1;
    this.rateForms = uwProducts.map(
      (uwProduct) => new UwProductRatesForm({ uwProduct, ratesType: finalRatesIneligible ? 'original' : 'final' }),
    );
    if (finalRatesIneligible) {
      this.setRateSource('original');
      this.restoreInitialRates();
      this._setCannotSelectFinalRates(true);
    }
  }

  constructor(props: Props) {
    super({
      products: props.uwProducts.map((uwProduct) => ({
        productName: uwProduct.name,
        rates: uwProduct.original.rates,
        portfolioId: uwProduct.portfolioId,
        productId: uwProduct.productId,
        bcIndex: uwProduct.bcIndex,
      })),
    });
    makeObservable<
      UwRatesOverriderDelegate,
      '_selectedRateSource' | '_cannotSelectFinalRates' | '_setCannotSelectFinalRates'
    >(this, {
      _selectedRateSource: observable,
      _cannotSelectFinalRates: observable,
      selectedRateSource: computed,
      setRateSource: action.bound,
      createRateForms: action.bound,
      _setCannotSelectFinalRates: action.bound,
      cannotSelectFinalRates: computed,
    });
  }
}
